"use client";
import React, { createContext, useState, useContext, useCallback } from 'react';
import log from "logbeacon";
import { SlotContextType, SlotProviderProps } from './types';

// 默认的上下文值（用于生产环境）
const defaultContextValue: SlotContextType = {
  slots: {},
  registerSlot: () => {
    // 生产环境静默处理
    if (process.env.NODE_ENV === 'development') {
      log.warn('[文案]registerSlot: SlotProvider 未找到，操作被忽略');
    }
  },
  unregisterSlot: () => {
    // 生产环境静默处理
    if (process.env.NODE_ENV === 'development') {
      log.warn('[文案]unregisterSlot: SlotProvider 未找到，操作被忽略');
    }
  },
};

// 创建 Slot 上下文
const SlotContext = createContext<SlotContextType | undefined>(undefined);

/**
 * Slot Provider 组件
 * 提供插槽内容的注册和管理功能
 */
export const SlotProvider: React.FC<SlotProviderProps> = ({ children }) => {
  // 存储所有插槽内容的状态
  const [slots, setSlots] = useState<Record<string, React.ReactNode>>({});

  // 注册插槽内容的回调函数
  const registerSlot = useCallback((id: string, content: React.ReactNode) => {
    setSlots(prevSlots => ({
      ...prevSlots,
      [id]: content,
    }));
  }, []);

  // 注销插槽内容的回调函数
  const unregisterSlot = useCallback((id: string) => {
    setSlots(prevSlots => {
      const newSlots = { ...prevSlots };
      delete newSlots[id];
      return newSlots;
    });
  }, []);

  // 上下文值
  const contextValue: SlotContextType = {
    slots,
    registerSlot,
    unregisterSlot,
  };

  return (
    <SlotContext.Provider value={contextValue}>
      {children}
    </SlotContext.Provider>
  );
};

/**
 * 使用 Slot 上下文的自定义 Hook
 * @returns SlotContextType 插槽上下文
 * @throws Error 如果在 SlotProvider 外部使用（仅开发环境）
 */
export const useSlotContext = (): SlotContextType => {
  const context = useContext(SlotContext);
  
  if (!context) {
    // 仅在开发环境抛出错误，生产环境返回默认值
    // if (process.env.NODE_ENV === 'development') {
    //   throw new Error('useSlotContext 必须在 SlotProvider 内部使用');
    // }
    // 生产环境返回默认值，避免应用崩溃
    return defaultContextValue;
  }
  
  return context;
};
